/**
 * Animated progress indicator for onboarding steps
 *
 * Shows pill-shaped dots representing each step.
 * Active/completed steps use accent color, inactive use border color.
 */

import { View, StyleSheet } from 'react-native';
import Animated, { useAnimatedStyle, withTiming } from 'react-native-reanimated';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';

type OnboardingStepIndicatorProps = {
  currentStep: number;
  totalSteps: number;
};

export function OnboardingStepIndicator({
  currentStep,
  totalSteps,
}: OnboardingStepIndicatorProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  return (
    <View style={styles.container}>
      {Array.from({ length: totalSteps }, (_, index) => (
        <StepDot
          key={index}
          isActive={index < currentStep}
          activeColor={palette.primary}
          inactiveColor={palette.borderMuted}
        />
      ))}
    </View>
  );
}

type StepDotProps = {
  isActive: boolean;
  activeColor: string;
  inactiveColor: string;
};

function StepDot({ isActive, activeColor, inactiveColor }: StepDotProps) {
  const animatedStyle = useAnimatedStyle(() => ({
    backgroundColor: withTiming(isActive ? activeColor : inactiveColor, {
      duration: 250,
    }),
  }));

  return <Animated.View style={[styles.dot, animatedStyle]} />;
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: Spacing.lg,
    gap: Spacing.sm,
  },
  dot: {
    width: 32,
    height: 8,
    borderRadius: BorderRadius.xs,
  },
});
